export interface AutomationPlatformOption {
  label: string;
  value: string;
  image?: string;
  comingSoonDescription?: string;
  disabled?: boolean;
}

export const INPUT_APPLICATION_ID = "application-id";
export const INPUT_SOLUTION_ID = "solution-id";
/**
 * Inputs a SmartSuite action describes for the fields of the table it works
 * on, one per field, named by the field's slug. They belong to that table:
 * when the action's records start coming from another one, they are gone.
 */
export const FIELD_INPUT_PREFIX = "field-";
export const ROUTE_AUTOMATION_LIST = "list";
export const ROUTE_AUTOMATION_FORM = "automation-form";
export const INPUT_EMAILS = "send-to-input";
export const FIND_RECORD_LIMIT_SORT_ID = "limitSort";
export const FIND_RECORD_LIMIT_ID = "find-record-additional";
export const FIND_RECORDS_LIMIT = 100;
export const WEBHOOKS_URL = "request-url";
export const WEBHOOKS_METHOD = "http-method";
export const WEBHOOKS_REQUEST_CONTENT = "request-content";
export const WHEN_BUTTON_IS_CLICKED_BUTTON_SLUG = "button-slug";
export const WHEN_BUTTON_IS_CLICKED_BUTTON_LABEL = "button-label";
export const DOCS_AUTOMATOR_AUTOMATION = "docsautomator_automation";

export const OUTPUT_APPLICATION_ID = "application_id";
export const OUTPUT_TITLE = "title";
export const RECORD_ID = "record_id";

export const WHEN_BUTTON_IS_CLICKED_TRIGGER_ID = "when-button-is-clicked";

const MS_TEAMS_ACTIONABLE_NOTIFICATION_ACTION =
  "ms-teams-send-actionable-notification";

export const ACTIONS_SKIP_MERGING = [
  "find-records",
  "create-record",
  "update-record",
  "comment-record",
  MS_TEAMS_ACTIONABLE_NOTIFICATION_ACTION,
];

export const RECORD_CREATED_TRIGGER_IDS = [
  "created-record",
  "updated-record",
  "form-submitted",
  "matches-condition",
  "email-received",
  WHEN_BUTTON_IS_CLICKED_TRIGGER_ID,
];
export const TRIGGERS_WITH_APPLICATIONS = [
  "created-record",
  "form-submitted",
  "updated-record",
  "matches-condition",
  WHEN_BUTTON_IS_CLICKED_TRIGGER_ID,
];

export enum Integrations {
  smartsuite = "ssi",
  slack = "slki",
  twilio = "twil",
  webhooks = "webhooks",
  google = "goog",
  googlesheets = "googlesheets",
  googledrive = "googledrive",
  googlecalendar = "googleCalendar",
  outlook = "outlook",
  salesforce = "salesforce",
  intercom = "intercom",
  hubspot = "hubspot",
  microsoftTeams = "microsoftTeams",
  monday = "monday",
  jira = "jira",
  microsoft = "mic",
  docsautomator = "docsaut",
  generatePdf = "gen-pdf",
  ai = "ai",
  loop = "loop",
  o365 = "o365",
  msTeams = "ms-teams",
}

export enum GoogleIntegrationActions {
  gmail = "gmail",
}

const SSI_INTEGRATIONS = new Set([
  process.env.REACT_APP_SMART_SUITE_INTEGRATION_ID,
  "ssi-main",
  "ssi-hotfix",
  "ssi-staging",
  "ssi-dev",
  "gen-pdf-hotfix",
  "gen-pdf-dev",
  "gen-pdf-main",
  Integrations.webhooks,
]);

export const isSmartSuiteIntegration = (integrationId: string): boolean =>
  SSI_INTEGRATIONS.has(integrationId);

export const isMsTeamIntegration = (integrationId?: string): boolean =>
  integrationId === Integrations.msTeams;

export const isAiIntegration = (integrationId: string): boolean =>
  ["ai", "ai-hotfix", "ai-dev"].includes(integrationId);

export const smartSuiteIntegration =
  process.env.REACT_APP_SMART_SUITE_INTEGRATION_ID ?? "ssi-hotfix";

export enum TriggerLabel {
  createdRecord = "created-record",
  updatedRecord = "updated-record",
  matchesCondition = "matches-condition",
  schedule = "schedule",
  formSubmitted = "form-submitted",
  webhook = "webhook",
  whenButtonIsClicked = "when-button-is-clicked",
}

export enum ActionLabel {
  createRecord = "create-record",
  updateRecord = "update-record",
  deleteRecord = "delete-record",
  generatePdf = "gen-pdf",
  docsautomator = "docsautomator-generate-pdf",
  ai = "ai-custom-prompt",
  sendEmail = "send-email",
  sendEmailFromSmartsuite = "send-email-from-smartsuite",
  sendGmail = "send-gmail",
  findRecords = "find-records",
  mergeRecords = "merge-records",
  commentRecord = "comment-record",
  sendWebhook = "send-webhook",
  sendNotification = "send-notification",
  loop = "loop",
  msTeamsSendActionableNotification = "ms-teams-send-actionable-notification",
  msTeamsSendMessage = "ms-teams-send-message",
}

export const TriggersI18NKeys: Partial<Record<TriggerLabel | string, string>> =
  {
    [TriggerLabel.createdRecord]: "triggers.ssi-created-record",
    [TriggerLabel.updatedRecord]: "triggers.ssi-updated-record",
    [TriggerLabel.matchesCondition]: "triggers.ssi-matches-condition",
    [TriggerLabel.schedule]: "triggers.ssi-schedule",
    [TriggerLabel.formSubmitted]: "triggers.ssi-form-submitted",
    [TriggerLabel.webhook]: "triggers.ssi-webhook",
    [TriggerLabel.whenButtonIsClicked]: "triggers.ssi-when-button-is-clicked",
  };

export const ActionsI18NKeys: Partial<Record<ActionLabel | string, string>> = {
  [ActionLabel.createRecord]: "actions.ssi-create-record",
  [ActionLabel.updateRecord]: "actions.ssi-update-record",
  [ActionLabel.deleteRecord]: "actions.ssi-delete-record",
  [ActionLabel.generatePdf]: "actions.ssi-gen-pdf",
  [ActionLabel.docsautomator]: "actions.ssi-gen-pdf",
  [ActionLabel.sendEmail]: "actions.ssi-send-email",
  [ActionLabel.sendEmailFromSmartsuite]:
    "actions.ssi-send-email-from-smartsuite",
  [ActionLabel.sendNotification]: "actions.ssi-send-notification",
  [ActionLabel.sendGmail]: "actions.google-send-gmail",
  [ActionLabel.findRecords]: "actions.ssi-find-records",
  [ActionLabel.mergeRecords]: "actions.ssi-merge-records",
  [ActionLabel.commentRecord]: "actions.ssi-comment-record",
  [ActionLabel.sendWebhook]: "actions.ssi-send-webhook",
  [ActionLabel.msTeamsSendActionableNotification]:
    "actions.Send an actionable notification",
  [ActionLabel.msTeamsSendMessage]: "actions.Send a message",
};

export enum InputGroup {
  fieldsGroup = "ss-fields-group",
}

export enum ValidationType {
  required = "^.+$",
}

export const CURRENT_ITEM_OUTPUT_ID = "currentItem";

export const MENU_MAX_HEIGHT = 480;
export const MENU_MAX_HEIGHT_MARGIN = 100;

export const INPUT_ACTION_NAME_ID = "action-name";

export const INPUT_AI_ACTION_NAME_ID = "action_name";

export const REGEX_EMAIL =
  // eslint-disable-next-line no-useless-escape
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const integrationsIntercomIds: Record<string, number> = {
  "send-sms": 7920868,
  "send-message": 7920865,
  "ccs-slack-send-message": 7920865,
  "slack-send-actionable-notification": 7920865,
  "send-gmail": 7920970,
  "salesforce-create-record": 7920982,
  "salesforce-update-record": 7920982,
  "email-received": 8069518,
  "microsoft-teams-send-message-in-a-chat": 7920873,
  "microsoft-teams-send-message-in-a-channel": 7920873,
  "google-calendar-create-event": 7920972,
  "google-calendar-update-event": 7920972,
  "google-drive-upload-file": 7920975,
  "google-sheets-add-row": 7920976,
  "outlook-email-send-message": 7920978,
  "hubspot-create-record": 6837165,
  "hubspot-update-record": 6837165,
  "intercom-create-contact": 7920980,
  "intercom-update-contact": 7920980,
  "jira-create-issue": 7919107,
  "jira-update-issue": 7919107,
};

export const intergrationsMainIntercomId = 6846587;
